import type { ObjectId } from '@racemap/sdk/schema/base';
import type { BillableItem, EventDayBasedBillableItem } from '@racemap/sdk/schema/billing';
import { type EventStarterKey, EventStarterKeysSchema } from '@racemap/sdk/schema/eventStarterKey';
import type { StarterSyncReport } from '@racemap/sdk/schema/starters';
import { TrackerActivationType } from '@racemap/sdk/schema/trackers';
import type { BillableItemTypes } from '@racemap/sdk/schema/user';
import type { EventUserCredit, UserCreditPrototype } from '@racemap/sdk/schema/userCredits';
import { type EventsQuery, HTTPFetchError, RacemapAPIClient } from '@racemap/utilities/api-client';
import { type SportTypes, eventTypesByType } from '@racemap/utilities/consts/eventTypes';
import {
  DeviceClasses,
  EventTypes,
  HIGH_RESOLUTION_THRESHOLD,
} from '@racemap/utilities/consts/events';
import {
  formatNestedObject,
  parseNestedObject,
  parseTagObjectLegacy,
} from '@racemap/utilities/formattings/formatting';
import {
  formatTimeDuration,
  timeDurationToMilliseconds,
} from '@racemap/utilities/formattings/times';
import type { EventCycle } from '@racemap/utilities/functions/billing';
import { createPatchObj } from '@racemap/utilities/functions/createPatchObj';
import { getSplitsOfTrack } from '@racemap/utilities/functions/event';
import { isGroupEvent } from '@racemap/utilities/functions/utils';
import { validateStarterAppId } from '@racemap/utilities/functions/validateStarterAppId';
import { isDefined, isSamePointOnLineString } from '@racemap/utilities/functions/validation';
import { moveArrayEntry } from '@racemap/utilities/math';
import type { Brand } from '@racemap/utilities/types/brand';
import type {
  GroupEvent,
  RacemapEvent,
  RacemapStageEvent,
  RacemapStarter,
  RacemapStarterPrototype,
} from '@racemap/utilities/types/events';
import {
  type EventTrackObject,
  type GeoElementObject,
  type GeoObjects,
  type LayerObject,
  type POIObject,
  RacemapGeoTypes,
  type SplitObject,
} from '@racemap/utilities/types/geos';
import type { User_Legacy } from '@racemap/utilities/types/users';
import type { ID, PathInto, TypeOfPath } from '@racemap/utilities/types/utils';
import { Button, Modal, Space, Typography } from 'antd';
import { type Immutable, produce } from 'immer';
import Papa from 'papaparse';
import { toast } from 'react-toastify';
import { InterpolationIntervalConfirmModalContent } from 'src/components/InterpolationIntervalConfirmModalContent';
import { getPlayerUrl } from 'src/lib/getPlayerUrl';
import type { StoreApi } from 'zustand';
import { Tristate } from '../../components/utils/tristateTask';
import { type DraftState, type State, useStore } from '../reducers';
import type { FrontendBillableItemTypes } from './FrontendBillableItemTypes';
import {
  eventIsLocked,
  needsAConfirmation,
  prepareFrontendBillableItems,
  presentConfirmationDialog,
  presentEventLockedDialog,
} from './events_reducer_helper';

const apiClient = RacemapAPIClient.fromWindowLocation();
const { Title } = Typography;

export type CurrentEvent = Immutable<RacemapEvent> &
  Immutable<{
    childEvents: Array<RacemapEvent>;
    billableItems: Array<Immutable<FrontendBillableItem>>;
    keys: Map<string, EventStarterKey>;
    userCredits: Array<Immutable<EventUserCredit>>;
  }>;

export interface FrontendBillableItem extends Omit<BillableItem, 'type'> {
  startNewCycle?: boolean;
  cycle?: EventCycle;
  type: BillableItemTypes | FrontendBillableItemTypes;
}
export interface FrontendEventDayBasedBillableItem extends EventDayBasedBillableItem {
  activatedAddons: BillableItem['activatedAddons'];
  startNewCycle: boolean;
  cycle: EventCycle;
}

export interface EventsState {
  events: {
    items: Map<string, RacemapEvent | CurrentEvent>;
    currentEventId: null | string;
    savingState: Tristate;
    isLoading: boolean;
    isReady: boolean;
    showPaymentModal: boolean;
    showStarterSyncModal: boolean;
    lastStarterSyncReport: null | StarterSyncReport;
    getter: {
      currentEvent: () => Immutable<CurrentEvent> | null;
      childEvents: (parentId?: ID) => Array<Immutable<RacemapEvent>>;
      parentEvent: (parentId?: ID) => Immutable<GroupEvent> | null;
      connectedBrands: (eventId?: ID) => Array<Immutable<Brand>>;
      rrEventID: (eventId?: ID) => string | null;
    };
    actions: {
      loadEvents: (options: EventsQuery) => Promise<void>;
      loadEvent: (eventId: ID) => Promise<RacemapEvent>;
      loadChildEvents: (parentId: ID, options?: EventsQuery) => Promise<void>;
      addEvent: (newEvent: Partial<RacemapEvent>) => Promise<RacemapEvent | undefined>;
      addStage: (stageGroupId: ObjectId) => Promise<RacemapStageEvent | undefined>;
      addContests: (
        eventIdsToAdd: Array<ObjectId>,
        contestGroupId: ObjectId,
      ) => Promise<Array<ObjectId>>;
      removeContests: (
        eventIdsToRemove: Array<ObjectId>,
        contestGroupId: ObjectId,
      ) => Promise<Array<ObjectId>>;
      swapChildEvents: (e1: Immutable<RacemapEvent>, e2: Immutable<RacemapEvent>) => Promise<void>;
      loadCurrentEvent: (eventId: string) => Promise<RacemapEvent>;
      loadCurrentStarters: (eventId: string) => Promise<void>;
      clearCurrentEvent: () => void;
      updateEvent: <P extends PathInto<RacemapEvent | CurrentEvent>>(
        eventId: string,
        update: Array<{ key: P; newValue: TypeOfPath<RacemapEvent | Immutable<RacemapEvent>, P> }>,
      ) => Promise<void>;
      deleteEvent: (eventId: string) => Promise<void>;
      addStarters: (
        eventId: string,
        newStarter: Array<Partial<RacemapStarter> | RacemapStarterPrototype>,
      ) => Promise<void>;
      importStartersCSVFile: (eventId: string, file: File) => Promise<void>;
      changeStarterPositions: (
        eventId: string,
        moves: Array<{ currentPosition: number; newPosition: number }>,
      ) => Promise<void>;
      updateStarters: (starterIds: Array<string>, patch: Partial<RacemapStarter>) => Promise<void>;
      deleteStarters: (
        eventId: string,
        starterIds: Array<string> | Array<Immutable<string>>,
      ) => Promise<void>;
      dearchiveTracker: (trackerId: string) => Promise<void>;
      updateGeoElements: (newElements: Immutable<GeoObjects>) => Promise<void>;
      reorderGeoElements: (
        element: Immutable<POIObject | EventTrackObject>,
        targetIndex: number,
      ) => Promise<void>;
      updateGeoElement: (
        newElement: Partial<GeoElementObject> | Immutable<Partial<GeoElementObject>>,
      ) => Promise<void>;
      createGeoElement: (
        newElement: Partial<GeoElementObject> | Immutable<Partial<GeoElementObject>>,
      ) => Promise<void>;
      deleteGeoElement: (element: Immutable<GeoElementObject>) => Promise<void>;
      deleteGeoElements: (elements: Array<Immutable<GeoElementObject>>) => Promise<void>;
      generateStarterKeys: (eventId: string, count: number) => Promise<void>;
      changeShadowtrack: (eventId: string, shadowtrackId: string | null) => Promise<void>;
      createEventSpecificUserCredit: (
        eventId: string | ObjectId,
        item: UserCreditPrototype,
      ) => Promise<void>;
      removeBillableItem: (eventId: string, itemId: string) => Promise<void>;
      removeEventSpecificUserCredit: (eventId: string, creditId: ObjectId) => Promise<void>;
      syncStartersOfEvent: (options?: { eventId?: string; showToast?: boolean }) => Promise<void>;

      openPaymentModal: () => void;
      closePaymentModal: () => void;
      openStarterSyncModal: () => void;
      closeStarterSyncModal: () => void;
    };
  };
}

export const createEventStore = (
  set: StoreApi<State>['setState'],
  get: StoreApi<State>['getState'],
): EventsState => ({
  events: {
    items: new Map(),
    isReady: false,
    isLoading: false,
    currentEventId: null,
    savingState: Tristate.NORMAL,
    showPaymentModal: false,
    showStarterSyncModal: false,
    lastStarterSyncReport: null,
    getter: {
      currentEvent: () => {
        const s = get();
        const eventId = s.events.currentEventId;
        if (eventId == null) return null;
        return (s.events.items.get(eventId) as CurrentEvent) || null;
      },
      childEvents: (parentId) => {
        const s = get();
        const compId = parentId != null ? parentId : s.events.currentEventId;

        return Array.from(s.events.items.values()).filter(
          (e) => e.parent != null && e.parent === compId,
        );
      },
      parentEvent: (parentId) => {
        const s = get();
        let compId: string | undefined = parentId;
        if (parentId == null) {
          const currentEvent = s.events.getter.currentEvent();
          compId = currentEvent?.parent || undefined;
        }

        if (compId == null) return null;
        const parentEvent = s.events.items.get(compId) || null;
        if (parentEvent == null)
          throw new Error(`Found no parent event for the setted parent id: ${compId}!`);
        if (!isGroupEvent(parentEvent))
          throw new Error(
            `The event ${compId} should be an group event, but has the type ${parentEvent.type}!`,
          );

        return parentEvent;
      },
      connectedBrands: (eventId) => {
        if (eventId == null) return [];
        const s = get();
        const event = s.events.items.get(eventId);
        if (!event) return [];

        return Array.from(s.brands.items.values()).filter((b) =>
          event.modules.brandedAppIds.includes(b.id),
        );
      },
      rrEventID: (eventId) => {
        let event: Immutable<RacemapEvent | CurrentEvent> | null = null;
        if (eventId == null) {
          event = get().events.getter.currentEvent();
        } else {
          event = get().events.items.get(eventId) || null;
        }

        if (event == null) return null;
        if (event.type === EventTypes.STAGE && event.parent != null) {
          // stages use the race result event id of the group
          const parentEvent = get().events.getter.parentEvent(event.parent);
          return parentEvent?.integrations.raceresult.eventId || null;
        }

        return event.integrations.raceresult.eventId;
      },
    },
    actions: {
      loadEvents: async ({ filter = 'can-edit', show = ['hidden'], ...options }) => {
        set(
          produce((s) => {
            s.events.isLoading = true;
          }),
        );
        const events = await apiClient.getEvents({
          filter,
          show,
          ...options,
        });

        set(
          produce((s) => {
            for (const event of events) {
              s.events.items.set(event.id, event);
            }

            s.events.isLoading = false;
            s.events.isReady = true;
          }),
        );
      },
      loadEvent: async (eventId) => {
        const event = await apiClient.getEvent(eventId);

        set(
          produce((s) => {
            s.events.items.set(eventId, event);
          }),
        );
        return event;
      },
      loadChildEvents: async (
        parentId,
        { filter = 'can-edit', show = ['hidden'] }: EventsQuery = {},
      ) => {
        const childEvents = await apiClient.getEvents({
          filter,
          show,
          findByParentId: parentId,
        });

        set(
          produce((s) => {
            for (const event of childEvents) {
              s.events.items.set(event.id, event);
            }
          }),
        );
      },
      loadCurrentEvent: async (eventId) => {
        const [event, starters, geos, keys, billableItems, userCredits] = await Promise.all([
          apiClient.getEvent(eventId),
          apiClient.getEventStarters(eventId, { noFilter: true }),
          apiClient.getEventGeo(eventId),
          apiClient.getEventStarterKeys(eventId),
          apiClient.getEventBillableItems(eventId),
          apiClient.getEventUserCredits(eventId),
        ]);

        let childEvents: Array<RacemapEvent> = [];
        if (event.type === EventTypes.STAGE_GROUP) {
          childEvents = await apiClient.getStageGroupStages(event.id);
        } else if (event.type === EventTypes.CONTEST_GROUP) {
          childEvents = await apiClient.getContestGroupContests(event.id);
        }

        if (
          (event.type === EventTypes.CONTEST || event.type === EventTypes.STAGE) &&
          event.parent != null
        ) {
          await get().events.actions.loadEvent(event.parent);
        }
        // append billing cycle infos to billable items
        const preparedBillableItems = prepareFrontendBillableItems(billableItems, event);
        const keysArray = EventStarterKeysSchema.parse(keys);

        const currentEvent: CurrentEvent = {
          ...event,
          keys: new Map(keysArray.map((key) => [key.key, key])),
          starters: new Map(starters.map((starter) => [starter.id, starter])),
          geo: {
            ...event.geo,
            features: geos.features,
            shadowtrackId: event.geo?.shadowtrackId || null,
          },
          billableItems: preparedBillableItems,
          childEvents,
          userCredits,
        };

        set(
          produce((s) => {
            s.events.currentEventId = event.id;
            s.events.items.set(event.id, currentEvent);
            for (const child of childEvents) {
              s.events.items.set(child.id, child);
            }
          }),
        );
        return event;
      },
      loadCurrentStarters: async (eventId) => {
        const [starters, keys] = await Promise.all([
          apiClient.getEventStarters(eventId, { noFilter: true }),
          apiClient.getEventStarterKeys(eventId),
        ]);

        let currentEventState = get().events.items.get(eventId);
        if (currentEventState == null) {
          currentEventState = await get().events.actions.loadEvent(eventId);
        }

        if (currentEventState == null) {
          throw new Error(`Fail Event Update. Event with eventId ${eventId} is unknown.`);
        }

        const keysMap = new Map(keys.map((key) => [key.key, key]));
        const startersMap = new Map(starters.map((starter) => [starter.id, starter]));

        set(
          produce((s) => {
            if (currentEventState == null) return;

            s.events.currentEventId = currentEventState.id;
            s.events.items.set(currentEventState.id, {
              ...currentEventState,
              starters: startersMap,
              keys: keysMap,
            });
          }),
        );
      },
      clearCurrentEvent: () => {
        set(
          produce((s: DraftState) => {
            s.events.currentEventId = null;
            s.events.savingState = Tristate.NORMAL;
            s.events.lastStarterSyncReport = null;
          }),
        );
      },
      addEvent: async (event) => {
        try {
          set(
            produce((s) => {
              s.events.savingState = Tristate.SPINNING;
            }),
          );
          const createdEvent = await apiClient.createEvent(event);
          set(
            produce((s) => {
              s.events.items.set(createdEvent.id, createdEvent);
              s.events.savingState = Tristate.NORMAL;
            }),
          );

          return createdEvent;
        } catch (error) {
          set(
            produce((s) => {
              s.events.isSaving = Tristate.FAILURE;
            }),
          );
          console.log(`Failed to add a event: ${error}`);
          toast.error('The attempt to add the event failed.');
        }
      },
      addStage: async (stageGroupId: ObjectId) => {
        try {
          set(
            produce((s) => {
              s.events.savingState = Tristate.SPINNING;
            }),
          );
          const newStage = await apiClient.addStageToStageGroup(stageGroupId);
          await get().events.actions.loadChildEvents(stageGroupId.toHexString());

          set(
            produce((s) => {
              s.events.savingState = Tristate.NORMAL;
            }),
          );

          return newStage;
        } catch (error) {
          set(
            produce((s) => {
              s.events.isSaving = Tristate.FAILURE;
            }),
          );
          console.log(`Failed to add a stage: ${error}`);
          toast.error('The attempt to add stage to stage group.');
        }
      },
      addContests: async (eventIdsToAdd: Array<ObjectId>, contestGroupId: ObjectId) => {
        try {
          set(
            produce((s) => {
              s.events.savingState = Tristate.SPINNING;
            }),
          );
          const addedEventIds = await apiClient.addContestsToContestGroup(
            contestGroupId,
            eventIdsToAdd,
          );
          await get().events.actions.loadChildEvents(contestGroupId.toHexString());

          set(
            produce((s) => {
              s.events.savingState = Tristate.NORMAL;
            }),
          );
          return addedEventIds;
        } catch (error) {
          set(
            produce((s) => {
              s.events.isSaving = Tristate.FAILURE;
            }),
          );
          console.log(`Failed to add a contest: ${error}`);
          toast.error('The attempt to add contest to contest group.');
          return [];
        }
      },
      removeContests: async (eventIdsToRemove: Array<ObjectId>, contestGroupId: ObjectId) => {
        const oldEvents = eventIdsToRemove.map((id) => get().events.items.get(id.toHexString()));
        try {
          set(
            produce((s) => {
              s.events.savingState = Tristate.SPINNING;
              // optimistic update
              eventIdsToRemove.forEach((eventId) => {
                const removeCandidat = s.events.items.get(eventId.toHexString());
                if (removeCandidat == null) return;

                removeCandidat.parent = null;
                removeCandidat.index = null;
                removeCandidat.type = EventTypes.REGULAR;
              });
            }),
          );
          const removedEventIds = await apiClient.removeContestsFromContestGroup(
            contestGroupId,
            eventIdsToRemove,
          );

          await Promise.all(
            eventIdsToRemove.map((eventId) =>
              get().events.actions.loadEvent(eventId.toHexString()),
            ),
          );

          set(
            produce((s) => {
              s.events.savingState = Tristate.NORMAL;
            }),
          );
          return removedEventIds;
        } catch (error) {
          set(
            produce((s) => {
              s.events.isSaving = Tristate.FAILURE;
              for (const event of oldEvents) {
                if (isDefined(event)) {
                  s.events.items.set(event.id, event);
                }
              }
            }),
          );
          console.log(`Failed to add a event: ${error}`);
          toast.error('The attempt to add the event failed.');

          return [];
        }
      },
      swapChildEvents: async (e1, e2) => {
        try {
          set(
            produce((s) => {
              // optimistic update
              s.events.items.get(e1.id).index = e2.index;
              s.events.items.get(e2.id).index = e1.index;
            }),
          );
          await get().events.actions.updateEvent(e1.id, [{ key: 'index', newValue: e2.index }]);
        } catch (error) {
          set(
            produce((s) => {
              s.events.items.get(e1.id).index = e1.index;
              s.events.items.get(e2.id).index = e2.index;
            }),
          );
          console.log(`Failed to swap events: ${error}`);
          toast.error('The attempt to swap the events failed.');
        }
      },
      updateEvent: async (eventId, updates) => {
        let currentEventState = get().events.items.get(eventId);
        try {
          if (currentEventState == null) {
            currentEventState = await get().events.actions.loadEvent(eventId);
          }

          if (currentEventState == null) {
            throw new Error(`Fail Event Update. Event with eventId ${eventId} is unknown.`);
          }

          const eventIdOfCurrentEvent = get().events.currentEventId;
          const isCurrent = eventId === eventIdOfCurrentEvent;
          const isChildOfCurrent = currentEventState.parent === eventIdOfCurrentEvent;

          // create the patch object, that only consist with the updates
          const protectedFields = [
            'id',
            'createdAt',
            'updatedAt',
            'keys',
            'billableItems',
            'childEvents',
          ];
          const patchObj = createPatchObj(
            updates,
            currentEventState as RacemapEvent,
            protectedFields,
          );

          if (patchObj.eventType != null) {
            patchObj.maxSpeed = eventTypesByType[patchObj.eventType].maxSpeed;
          }

          // Optimistic update
          set(
            produce((s: DraftState) => {
              s.events.savingState = Tristate.SPINNING;
              const currentEvent = s.events.items.get(currentEventState?.id || '');

              if (currentEvent != null && (isCurrent || isChildOfCurrent)) {
                s.events.items.set(currentEvent.id, {
                  ...currentEvent,
                  ...(patchObj as RacemapEvent),
                });
              }
            }),
          );

          if (isCurrent && eventIsLocked(patchObj, currentEventState)) {
            await presentEventLockedDialog(currentEventState);
            return;
          }

          if (isCurrent && needsAConfirmation(patchObj, currentEventState)) {
            await presentConfirmationDialog(patchObj, currentEventState);
          }

          if (
            isCurrent &&
            patchObj.playerOptions?.stepWidth != null &&
            patchObj.playerOptions?.stepWidth < HIGH_RESOLUTION_THRESHOLD &&
            currentEventState.playerOptions.stepWidth >= HIGH_RESOLUTION_THRESHOLD
          ) {
            await new Promise<void>((resolve, reject) => {
              Modal.confirm({
                title: 'Warning',
                content: <InterpolationIntervalConfirmModalContent />,
                onOk: () => resolve(),
                onCancel: () => reject(new Error('CANCEL')),
              });
            });
          }

          await apiClient.updateEvent(eventId, patchObj);

          // if the sport of the event change and we have 2 splits (start and finish)
          // and the first split has no sport then we update the first split with the
          // new sport and min/max speed
          if (patchObj.eventType != null) {
            const splits = getSplitsOfTrack(currentEventState);

            if (
              splits != null &&
              splits.length === 2 &&
              splits[0].properties.minSpeed === 0.1 &&
              splits[0].properties.maxSpeed === 360
            ) {
              const sport = eventTypesByType[patchObj.eventType as SportTypes];
              if (sport != null) {
                await apiClient.updateGeoPoint(splits[0].id, {
                  ...splits[0],
                  properties: {
                    ...splits[0].properties,
                    newSport: patchObj.eventType,
                    minSpeed: sport.minSpeed,
                    maxSpeed: sport.maxSpeed,
                  },
                });
              }
            }
          }

          // reload current Event
          if (eventIdOfCurrentEvent != null && (isCurrent || isChildOfCurrent)) {
            await get().events.actions.loadCurrentEvent(eventIdOfCurrentEvent);
          }

          set(
            produce((s) => {
              s.events.savingState = Tristate.NORMAL;
            }),
          );
        } catch (error) {
          const userCancelUpdate = error instanceof Error && error.message === 'CANCEL';
          const eventIsLocked = error instanceof Error && error.message === 'LOCKED';

          if (userCancelUpdate || eventIsLocked) {
            set(
              produce((s) => {
                if (currentEventState != null)
                  s.events.items.set(currentEventState.id, currentEventState);
                s.events.savingState = Tristate.NORMAL;
              }),
            );

            if (userCancelUpdate)
              toast.warn('The confirmation was declined. The changes were not saved.');
            if (eventIsLocked)
              toast.warn('The event times are locked. The changes were not saved.');
            return;
          }

          set(
            produce((s) => {
              if (currentEventState != null)
                s.events.items.set(currentEventState.id, currentEventState);
              s.events.savingState = Tristate.FAILURE;
            }),
          );
          console.error(`Failed to change a event: ${eventId}`);
          console.error(error);
          const serverMessage =
            error instanceof HTTPFetchError && error.serverError != null ? error.serverError : null;
          toast.error(
            serverMessage != null
              ? `Failed to update the event: ${serverMessage}`
              : `The attempt to update the event ${eventId} failed.`,
          );
        }
      },
      deleteEvent: async (eventId) => {
        try {
          set(
            produce((s) => {
              s.events.savingState = Tristate.SPINNING;
            }),
          );
          await apiClient.removeEvent(eventId);

          set(
            produce((s) => {
              s.events.items.delete(eventId);
            }),
          );
        } catch (error) {
          set(
            produce((s) => {
              s.events.savingState = Tristate.FAILURE;
            }),
          );
          console.log(`Failed to remove a event: ${error}`);
          toast.error(`The attempt to delete the event ${eventId} failed.`);
        }
      },
      addStarters: async (eventId, newStarters) => {
        try {
          set(
            produce((s) => {
              s.events.savingState = Tristate.SPINNING;
            }),
          );
          await apiClient.createStarters(newStarters, eventId);

          if (get().events.currentEventId === eventId) {
            const starters = await apiClient.getEventStarters(eventId, { noFilter: true });

            set(
              produce((s) => {
                const event = s.events.items.get(eventId);

                s.events.items.set(eventId, {
                  ...event,
                  starters,
                });
              }),
            );

            get().events.actions.loadCurrentEvent(eventId);
          }

          set(
            produce((s) => {
              s.events.savingState = Tristate.NORMAL;
            }),
          );
        } catch (error) {
          set(
            produce((s) => {
              s.events.savingState = Tristate.FAILURE;
            }),
          );
          console.log(`Failed to add a starter: ${error}`);
          if (error instanceof HTTPFetchError) {
            toast.error(
              `The attempt to add the starter ${newStarters.map((s) => s.name).join('/')} failed. ${error.serverError}`,
            );
          } else {
            toast.error(
              `The attempt to add the starter ${newStarters.map((s) => s.name).join('/')} failed.`,
            );
          }
        }
      },
      importStartersCSVFile: async (eventId, file) => {
        try {
          set(
            produce((s) => {
              s.events.savingState = Tristate.SPINNING;
            }),
          );
          const rawStarters = await parseStartersCSVFile(file);
          validateRawStarters(rawStarters);
          await get().events.actions.addStarters(eventId, rawStarters);

          set(
            produce((s) => {
              s.events.savingState = Tristate.NORMAL;
            }),
          );
        } catch (error) {
          set(
            produce((s) => {
              s.events.savingState = Tristate.FAILURE;
            }),
          );
          console.log('Failed to add a starter');
          console.error(error);
          toast.error(`The attempt to import the starters failed: ${error}`);
        }
      },
      changeStarterPositions: async (eventId, moves) => {
        try {
          set(
            produce((s) => {
              s.events.savingState = Tristate.SPINNING;
            }),
          );
          const events = get().events;
          const event = events.items.get(eventId);
          if (event == null) throw new Error(`Event with id ${eventId} not found!`);

          const starterIds =
            event.starters != null
              ? Array.from(event.starters.values()).map((s) => s.id)
              : (await apiClient.getEventStarters(eventId, { noFilter: true })).map((s) => s.id);

          let newStarterIds = starterIds;
          for (const move of moves) {
            newStarterIds = moveArrayEntry(newStarterIds, move.currentPosition, move.newPosition);
          }

          await events.actions.updateEvent(eventId, [
            { key: 'starterIds', newValue: newStarterIds },
          ]);

          set(
            produce((s) => {
              s.events.savingState = Tristate.NORMAL;
            }),
          );
        } catch (error) {
          set(
            produce((s) => {
              s.events.savingState = Tristate.FAILURE;
            }),
          );
          console.log(`Failed to move a starter: ${error}`);
          toast.error('The attempt to move the starter failed.');
        }
      },
      updateStarters: async (ids, starterUpdate) => {
        try {
          set(
            produce((s) => {
              s.events.savingState = Tristate.SPINNING;
            }),
          );

          const newStarters = await apiClient.updateStarters(ids, starterUpdate);

          const currentEvent = get().events.getter.currentEvent();
          if (currentEvent?.starters?.has(ids[0])) {
            set(
              produce((s) => {
                const event = s.events.items.get(currentEvent.id);
                for (const starter of newStarters) {
                  event.starters.set(starter.id, {
                    ...(event.starters.get(starter.id) || {}),
                    ...starter,
                  });
                }
                s.events.items.set(currentEvent.id, event);
              }),
            );
            await get().events.actions.loadCurrentStarters(currentEvent.id);
          }

          set(
            produce((s) => {
              s.events.savingState = Tristate.NORMAL;
            }),
          );
        } catch (error) {
          set(
            produce((s) => {
              s.events.savingState = Tristate.FAILURE;
            }),
          );

          if (!(error instanceof Error)) return;
          switch (error.constructor) {
            case HTTPFetchError: {
              const fetchError = error as HTTPFetchError;

              console.error(`Failed to update a starters: ${fetchError.message}`);
              if (fetchError.serverError != null) console.error(fetchError.serverError);
              toast.error(`The attempt to update the starters failed. ${fetchError.serverError}`);
              return;
            }
            default:
              console.error(error);
              toast.error('The attempt to update the starters failed. Error: UNKNOWN');
              return;
          }
        }
      },
      deleteStarters: async (eventId: string, starterIds: Array<string>) => {
        try {
          set(
            produce((s) => {
              s.events.savingState = Tristate.SPINNING;
            }),
          );
          await apiClient.removeStarters(starterIds, eventId);

          if (get().events.currentEventId === eventId) {
            const starters = await apiClient.getEventStarters(eventId, { noFilter: true });

            set(
              produce((s) => {
                const event = s.events.items.get(eventId);

                s.events.items.set(eventId, {
                  ...event,
                  starters: new Map(starters.map((starter) => [starter.id, starter])),
                });
              }),
            );

            get().events.actions.loadCurrentEvent(eventId);
          }

          set(
            produce((s) => {
              s.events.savingState = Tristate.NORMAL;
            }),
          );
        } catch (error) {
          set(
            produce((s) => {
              s.events.savingState = Tristate.FAILURE;
            }),
          );
          console.log(`Failed to delete a starter: ${error}`);
          toast.error('The attempt to delete the starter failed.');
        }
      },
      dearchiveTracker: async (trackerId) => {
        try {
          await apiClient.updateTracker(trackerId, {
            'activation.type': TrackerActivationType.ACTIVATED,
          });
          const currentEvent = get().events.getter.currentEvent();
          if (currentEvent == null) return;

          const starterOfDevice = Array.from(currentEvent.starters?.values() || []).find(
            (s) => s.deviceId === trackerId,
          );
          if (starterOfDevice != null) {
            set(
              produce((s: DraftState) => {
                const event = s.events.items.get(currentEvent.id);
                const starter = event?.starters?.get(starterOfDevice.id);
                if (starter?.deviceActivation != null) {
                  starter.deviceActivation.type = TrackerActivationType.ACTIVATED;
                }
              }),
            );
          }

          toast.success('Tracker dearchived successfully.');
          get().events.actions.loadCurrentEvent(currentEvent.id);
        } catch (error) {
          console.error(`Failed to dearchive tracker: ${error}`);
          toast.error(`The attempt to dearchive the tracker ${trackerId} failed.`);
        }
      },
      reorderGeoElements: async (element, targetIndex) => {
        const eventId = element.properties?.eventId;
        if (eventId == null) return;

        // The backend owns re-sequencing: it pulls this element out and re-inserts it at the
        // target slot, then normalizes the whole set to a contiguous 0..n-1. We send a single
        // PATCH for the moved element and mirror that reordering locally for an optimistic update.
        const racemapType = element.properties.racemapType;
        const features = get().events.items.get(eventId)?.geo?.features;
        if (!Array.isArray(features)) return;

        const siblings = features
          .filter((f) => f.properties?.racemapType === racemapType)
          .sort(
            (a, b) =>
              ((a.properties as { index?: number }).index ?? Number.POSITIVE_INFINITY) -
              ((b.properties as { index?: number }).index ?? Number.POSITIVE_INFINITY),
          );

        const fromIndex = siblings.findIndex((f) => f.id === element.id);
        if (fromIndex === -1) return;

        const reordered = [...siblings];
        const [moved] = reordered.splice(fromIndex, 1);
        reordered.splice(Math.max(0, Math.min(targetIndex, reordered.length)), 0, moved);

        // Remember previous order values so we can roll back if persistence fails.
        const previousIndexes = siblings.map((f) => ({
          id: f.id,
          index: (f.properties as { index?: number }).index,
        }));
        const nextIndexes = reordered.map((f, index) => ({ id: f.id, index }));

        const applyIndexes = (s: DraftState, indexes: Array<{ id: string; index?: number }>) => {
          const features = s.events.items.get(eventId)?.geo.features;
          if (!Array.isArray(features)) return;
          for (const { id, index } of indexes) {
            const feature = features.find((f) => f.id === id);
            if (feature != null) (feature.properties as { index?: number }).index = index;
          }
        };

        // Optimistic update: write the new order indices into the store immediately.
        set(
          produce((s: DraftState) => {
            s.events.savingState = Tristate.SPINNING;
            applyIndexes(s, nextIndexes);
          }),
        );

        try {
          const updated = {
            ...element,
            properties: { ...element.properties, index: targetIndex },
          };
          if (racemapType === RacemapGeoTypes.TRACK) {
            await apiClient.updateGeoLineString(element.id, updated as EventTrackObject);
          } else {
            await apiClient.updateGeoPoint(element.id, updated as POIObject);
          }

          if (eventId === get().events.currentEventId) {
            await get().events.actions.loadCurrentEvent(eventId);
          }
          set(
            produce((s) => {
              s.events.savingState = Tristate.NORMAL;
            }),
          );
        } catch (error) {
          set(
            produce((s: DraftState) => {
              s.events.savingState = Tristate.NORMAL;
              applyIndexes(s, previousIndexes);
            }),
          );
          console.error(`Failed to reorder geo elements: ${error}`);
          toast.error('The attempt to reorder the elements failed.');
        }
      },
      updateGeoElements: async (newFeatures) => {
        for (const feature of newFeatures) {
          try {
            const { id, properties } = feature;
            const { eventId } = properties || {};
            if (id == null || properties == null || eventId == null)
              throw new Error('Only can update geo elements with an id and properties!');

            const event = get().events.items.get(eventId);
            const existingElement = event?.geo.features?.find((f) => f.id === id);
            const existingPointOnLineString = event?.geo.features?.find((f) =>
              isSamePointOnLineString(f, feature),
            );

            if (existingElement != null) {
              await get().events.actions.updateGeoElement(feature);
            } else if (existingPointOnLineString != null) {
              await get().events.actions.updateGeoElement({
                ...existingPointOnLineString,
                properties: {
                  ...existingPointOnLineString.properties,
                  ...feature.properties,
                },
              });
            } else {
              await get().events.actions.createGeoElement(feature);
            }
          } catch (error) {
            if (
              error instanceof HTTPFetchError &&
              typeof error.serverError === 'string' &&
              error.serverError.startsWith('There is already a point on')
            ) {
              console.warn('Skipping duplicated point on linestring');
              continue;
            }

            console.error(error);
            toast.error(`Failed to update the geo element ${feature.id}`);
          }
        }
      },
      updateGeoElement: async (newElement) => {
        const { id, properties } = newElement;
        const { eventId } = properties || {};
        const isCurrentEvent = eventId === get().events.currentEventId;

        if (isCurrentEvent) {
          set(
            produce((s: DraftState) => {
              s.events.savingState = Tristate.SPINNING;

              const event = s.events.items.get(eventId);
              const features = event?.geo.features;
              const featureIndex = features?.findIndex((f) => f.id === id);

              if (Array.isArray(features) && featureIndex != null && featureIndex > -1) {
                Object.assign(features[featureIndex], newElement);
              }
            }),
          );
        }

        try {
          if (id == null || properties == null || eventId == null)
            throw new Error('Only can update geo elements with an id and properties!');

          const racemapType = properties.racemapType;

          switch (racemapType) {
            case RacemapGeoTypes.TRACK:
              await apiClient.updateGeoLineString(id, newElement as EventTrackObject);
              break;
            case RacemapGeoTypes.POI:
            case RacemapGeoTypes.SPLIT:
              await apiClient.updateGeoPoint(id, newElement as SplitObject | POIObject);
              break;
            case RacemapGeoTypes.EXTERNAL_LAYER:
              await apiClient.updateGeoLayer(id, newElement as LayerObject);
              break;
            default:
              throw new Error('Only can update geo elements of known types.');
          }

          if (isCurrentEvent) {
            await get().events.actions.loadCurrentEvent(eventId);
          }
          set(
            produce((s) => {
              s.events.savingState = Tristate.NORMAL;
            }),
          );
        } catch (error) {
          if (error instanceof Error) {
            toast.error('Failed to update a geo element: ');
            console.error('Failed to update a geo element: ', error.message);

            if (error instanceof HTTPFetchError) {
              console.error(error.serverError);
            } else {
              console.error(error);
            }
          }

          if (isCurrentEvent) {
            await get().events.actions.loadCurrentEvent(eventId);
          }
        }
      },
      createGeoElement: async (newElement) => {
        const { properties } = newElement;
        const { eventId } = properties || {};
        if (properties == null || eventId == null)
          throw new Error('Only can update geo elements with an id and properties!');

        const racemapType = properties.racemapType;
        const isCurrentEvent = eventId === get().events.currentEventId;

        switch (racemapType) {
          case RacemapGeoTypes.TRACK:
            await apiClient.createGeoLineString(newElement as EventTrackObject);
            break;
          case RacemapGeoTypes.POI:
          case RacemapGeoTypes.SPLIT:
            await apiClient.createGeoPoint(newElement as SplitObject | POIObject);
            break;
          case RacemapGeoTypes.EXTERNAL_LAYER:
            await apiClient.createGeoLayer(newElement as LayerObject);
            break;
          default:
            throw new Error('Only can update geo elements of known types.');
        }

        if (isCurrentEvent) {
          await get().events.actions.loadCurrentEvent(eventId);
        }
      },
      deleteGeoElement: async (element) => {
        const { id, properties } = element;
        const { eventId } = properties || {};
        if (id == null || properties == null || eventId == null)
          throw new Error('Only can update geo elements with an id and properties!');

        const racemapType = properties.racemapType;
        const isCurrentEvent = eventId === get().events.currentEventId;
        const features = get().events.items.get(eventId)?.geo?.features || [];

        set(
          produce((s) => {
            if (isCurrentEvent) {
              const event = s.events.items.get(eventId);
              event.geo.features = features.filter((f) => f.id !== id);
            }
          }),
        );

        try {
          switch (racemapType) {
            case RacemapGeoTypes.TRACK:
              await apiClient.removeGeoLineString(id);
              break;
            case RacemapGeoTypes.POI:
            case RacemapGeoTypes.SPLIT:
              await apiClient.removeGeoPoint(id);
              break;
            case RacemapGeoTypes.EXTERNAL_LAYER:
              await apiClient.removeGeoLayer(id);
              break;
            default:
              throw new Error('Only can remove geo elements of known types.');
          }
        } catch (error) {
          console.log(error);
          toast.error('Failed to remove the track from the map.');
          set(
            produce((s) => {
              if (isCurrentEvent) {
                const event = s.events.items.get(eventId);
                event.geo.features = features;
              }
            }),
          );
        }

        if (isCurrentEvent) {
          await get().events.actions.loadCurrentEvent(eventId);
        }
      },
      deleteGeoElements: async (elements) => {
        if (elements.length === 0) return;

        const eventId = elements[0].properties?.eventId;
        if (eventId == null) throw new Error('Only can delete geo elements with an eventId!');

        const validElements = elements.filter(
          (element) => element.id != null && element.properties?.eventId === eventId,
        );
        if (validElements.length === 0) return;

        const idsToRemove = new Set(validElements.map((element) => element.id));
        const isCurrentEvent = eventId === get().events.currentEventId;
        const features = get().events.items.get(eventId)?.geo?.features || [];

        set(
          produce((s) => {
            if (isCurrentEvent) {
              const event = s.events.items.get(eventId);
              event.geo.features = features.filter((f) => !idsToRemove.has(f.id));
            }
          }),
        );

        try {
          const results = await Promise.allSettled(
            validElements.map((element) => {
              switch (element.properties.racemapType) {
                case RacemapGeoTypes.TRACK:
                  return apiClient.removeGeoLineString(element.id);
                case RacemapGeoTypes.POI:
                case RacemapGeoTypes.SPLIT:
                  return apiClient.removeGeoPoint(element.id);
                case RacemapGeoTypes.EXTERNAL_LAYER:
                  return apiClient.removeGeoLayer(element.id);
                default:
                  return Promise.reject(new Error('Only can remove geo elements of known types.'));
              }
            }),
          );

          if (results.some((result) => result.status === 'rejected')) {
            throw new Error('Failed to remove one or more geo elements.');
          }
        } catch (error) {
          console.log(error);
          toast.error('Failed to remove the elements from the map.');
          set(
            produce((s) => {
              if (isCurrentEvent) {
                const event = s.events.items.get(eventId);
                event.geo.features = features;
              }
            }),
          );
        }

        if (isCurrentEvent) {
          await get().events.actions.loadCurrentEvent(eventId);
        }
      },
      generateStarterKeys: async (eventId, count) => {
        try {
          set(
            produce((s) => {
              s.events.savingState = Tristate.SPINNING;
            }),
          );
          const keys = await apiClient.createEventStarterKeys(eventId, count);
          const keysMap = new Map(keys.map((key) => [key.key, key]));

          if (get().events.currentEventId === eventId) {
            set(
              produce((s) => {
                const event = s.events.items.get(eventId);

                s.events.items.set(eventId, {
                  ...event,
                  keys: new Map([...(event?.keys ?? []), ...keysMap]), // append new keys to existing keys
                });
              }),
            );

            get().events.actions.loadCurrentEvent(eventId);
          }

          set(
            produce((s) => {
              s.events.savingState = Tristate.NORMAL;
            }),
          );
        } catch (error) {
          set(
            produce((s) => {
              s.events.savingState = Tristate.FAILURE;
            }),
          );
          console.log(`Failed to create keys for starter: ${error}`);
          toast.error('Failed to create keys for starter.');
        }
      },

      changeShadowtrack: async (eventId, shadowtrackId) => {
        const event = get().events.items.get(eventId);
        const isCurrentEvent = eventId === get().events.currentEventId;
        if (event == null) return;

        if (event.geo == null)
          throw new Error('Only can update the shadowtrack of the current event.');

        get().events.actions.updateEvent(eventId, [
          {
            key: 'geo',
            newValue: { ...event.geo, shadowtrackId },
          },
        ]);

        if (isCurrentEvent) {
          await get().events.actions.loadCurrentEvent(eventId);
        }
      },

      createEventSpecificUserCredit: async (eventId, item) => {
        try {
          set(
            produce((s) => {
              s.events.savingState = Tristate.SPINNING;
            }),
          );
          await apiClient.createUserCredit(item);

          if (get().events.currentEventId === eventId.toString()) {
            await get().events.actions.loadCurrentEvent(eventId.toString());
          }

          set(
            produce((s) => {
              s.events.savingState = Tristate.NORMAL;
            }),
          );
        } catch (error) {
          set(
            produce((s) => {
              s.events.savingState = Tristate.FAILURE;
            }),
          );
          console.log(`Failed to add user credit: ${error}`);
          toast.error('Failed to add user credit.');
        }
      },

      removeBillableItem: async (eventId, itemId) => {
        try {
          set(
            produce((s) => {
              s.events.savingState = Tristate.SPINNING;
            }),
          );
          await apiClient.removeBillableItem(itemId);

          if (get().events.currentEventId === eventId) {
            const billableItems = await apiClient.getEventBillableItems(eventId);

            set(
              produce((s) => {
                const event = s.events.items.get(eventId);

                s.events.items.set(eventId, {
                  ...event,
                  billableItems: prepareFrontendBillableItems(billableItems, event),
                });
              }),
            );

            get().events.actions.loadCurrentEvent(eventId);
          }

          set(
            produce((s) => {
              s.events.savingState = Tristate.NORMAL;
            }),
          );
        } catch (error) {
          set(
            produce((s) => {
              s.events.savingState = Tristate.FAILURE;
            }),
          );
          console.log(`Failed to remove the billable item: ${error}`);
          toast.error('Failed to remove the billable item.');
        }
      },

      removeEventSpecificUserCredit: async (eventId, creditId) => {
        try {
          set(
            produce((s) => {
              s.events.savingState = Tristate.SPINNING;
            }),
          );
          await apiClient.removeUserCredit({ userCreditId: creditId });

          if (get().events.currentEventId === eventId.toString()) {
            await get().events.actions.loadCurrentEvent(eventId.toString());
          }

          set(
            produce((s) => {
              s.events.savingState = Tristate.NORMAL;
            }),
          );
        } catch (error) {
          set(
            produce((s) => {
              s.events.savingState = Tristate.FAILURE;
            }),
          );
          console.log(`Failed to remove the user credit: ${error}`);
          toast.error('Failed to remove the user credit.');
        }
      },

      syncStartersOfEvent: async ({ eventId, showToast = true } = {}) => {
        const targetId = eventId || get().events.currentEventId;
        if (targetId == null) {
          toast.error('No event selected to sync the starters.');
          return;
        }

        try {
          set(
            produce((s) => {
              s.events.savingState = Tristate.SPINNING;
            }),
          );
          const res = await apiClient.syncStarterImports({ eventIds: [targetId] });
          await get().events.actions.loadCurrentStarters(targetId);

          set(
            produce((s: DraftState) => {
              s.events.savingState = Tristate.NORMAL;
              s.events.lastStarterSyncReport = res;
            }),
          );

          const getToastContent = (title: string, description: string) => (
            <Space direction="vertical">
              <Title level={5} style={{ margin: 0 }}>
                {title}
              </Title>
              {description}
              <Button
                block
                variant="link"
                size="small"
                onClick={() => get().events.actions.openStarterSyncModal()}
              >
                Show report
              </Button>
            </Space>
          );

          if (!showToast) return;

          if (res.status === 'success') {
            toast.success(getToastContent('Sync successful', 'Starters synced successfully.'));
          } else if (res.status === 'skipped') {
            toast.info(getToastContent('Sync skipped', 'No starters needed to be synced.'));
          } else if (res.status === 'partial') {
            toast.warn(getToastContent('Partial failure', 'Some starters could not be synced.'));
          } else {
            toast.error(getToastContent('Sync failed', 'The starters could not be synced.'));
          }
        } catch (error) {
          set(
            produce((s) => {
              s.events.savingState = Tristate.FAILURE;
            }),
          );
          console.log(`Failed to sync starters: ${error}`);
          toast.error('The attempt to sync the starters failed.');
        }
      },

      openPaymentModal: () => {
        set(
          produce((s: DraftState) => {
            s.events.showPaymentModal = true;
          }),
        );
      },
      closePaymentModal: () => {
        set(
          produce((s: DraftState) => {
            s.events.showPaymentModal = false;
          }),
        );
      },
      openStarterSyncModal: () => {
        set(
          produce((s: DraftState) => {
            s.events.showStarterSyncModal = true;
          }),
        );
      },
      closeStarterSyncModal: () => {
        set(
          produce((s: DraftState) => {
            s.events.showStarterSyncModal = false;
          }),
        );
      },
    },
  },
});

type StarterCSV = Array<string>;

function parseStartersCSVFile(file: File): Promise<Array<Partial<RacemapStarter>>> {
  return new Promise((resolve, reject) => {
    Papa.parse<StarterCSV>(file, {
      skipEmptyLines: true,
      complete(results) {
        // Here we have an csv with at least one data-row and 2 data-columns
        // Format has to be:
        //  startnumber;name;imei;device_type;device_class;marker_color
        //  12;Neppi;4106012717;LK106;Tracker;#fff
        //  24;Peter;4106012718;LK106;Tracker;#fff

        if (results.errors.length > 0) {
          reject(results.errors);
          return;
        }

        if (results.data.length <= 1) {
          reject(new Error('Too few rows'));
          return;
        }

        if (results.data[0].length < 2) {
          reject(new Error('Too few columns'));
          return;
        }

        const headerRow = results.data[0].map((h) => h.trim());

        const columnMapping: Record<string, number> = {};
        const headers = [
          'starter_id',
          'startnumber',
          'name',
          'imei',
          'device_type',
          'device_class',
          'device_id',
          'marker_color',
          'tags', // legacy, use the nested object collumns format instead
          'startTime',
          'endTime',
          'manualResult',
          'key',
        ];
        for (const header of headers) {
          const index = headerRow.indexOf(header);
          if (index < 0) continue;

          columnMapping[header] = index;
        }

        const nestedObjectsMapping: Record<string, Record<string, number>> = {};
        const nestedObjectHeadersPrefix = ['times', 'tags'];
        for (const headerPrefix of nestedObjectHeadersPrefix) {
          for (const csvHeader of headerRow) {
            if (
              csvHeader.startsWith(`${headerPrefix}_`) ||
              csvHeader.startsWith(`${headerPrefix}.`)
            ) {
              if (nestedObjectsMapping[headerPrefix] == null)
                nestedObjectsMapping[headerPrefix] = {};
              const index = headerRow.indexOf(csvHeader);

              nestedObjectsMapping[headerPrefix][csvHeader] = index;
            }
          }
        }

        for (const col of ['startnumber', 'name']) {
          if (columnMapping[col] == null) {
            reject(new Error(`Column ${col} missing.`));
            return;
          }
        }

        if (columnMapping.tags != null && nestedObjectsMapping.tags != null) {
          throw new Error(
            'You cant use the old and the new format to store the tags in the csv. Remove the legacy format and try again.',
          );
        }

        // this is for downwards compatibility, if someone used the old format
        // while parsing the end time of a starter, we transform it to the new finish property
        // of the nested times object
        const replaceEndByFinish = (timesNode?: Record<string, string>) => {
          if (timesNode != null && timesNode.end != null && timesNode.finish == null) {
            timesNode.finish = timesNode.end;
            // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
            delete timesNode.end;
          }
          return timesNode;
        };

        resolve(
          results.data.slice(1).map((row) => ({
            id: getValueOfRow(row, columnMapping.starter_id),
            startNumber: getValueOfRow(row, columnMapping.startnumber),
            name: getValueOfRow(row, columnMapping.name),
            appId: getValueOfRow(row, columnMapping.imei),
            deviceType: getValueOfRow(row, columnMapping.device_type),
            deviceClass:
              (getValueOfRow(row, columnMapping.device_class) as DeviceClasses) || undefined,
            markerColor: getValueOfRow(row, columnMapping.marker_color),
            tags: {
              ...parseTagObjectLegacy(getValueOfRow(row, columnMapping.tags) || ''),
              ...parseNestedObject(row, nestedObjectsMapping.tags || {}),
            },
            startTime: getValueOfRow(row, columnMapping.startTime),
            endTime: getValueOfRow(row, columnMapping.endTime),
            manualFinishDuration:
              getValueOfRow(row, columnMapping.manualResult) != null
                ? timeDurationToMilliseconds(getValueOfRow(row, columnMapping.manualResult) || '')
                : undefined,
            key: getValueOfRow(row, columnMapping.key),
            deviceId: getValueOfRow(row, columnMapping.device_id) || null,
            times: replaceEndByFinish(parseNestedObject(row, nestedObjectsMapping.times || {})),
          })),
        );
      },
    });
  });
}

function getValueOfRow(row: StarterCSV, index: number): string | undefined {
  const value = row[index];

  if (value == null) return undefined;
  return value.trim();
}

export function buildStartersCSVFile(
  starters: Array<RacemapStarter>,
  user: Immutable<User_Legacy>,
  event: Immutable<RacemapEvent>,
) {
  let csvString =
    'startnumber;name;imei;device_type;device_class;marker_color;startTime;endTime;manualResult;key;starter_id;device_id;device_name';
  const possibleTags = Array.from(
    new Set(
      starters.reduce<Array<string>>((prev, s) => {
        return produce(prev, (draft) => {
          draft.push(...Array.from(Object.keys(s.tags)));
        });
      }, []),
    ),
  );
  if (possibleTags.length > 0) {
    csvString += `;tags.${possibleTags.join(';tags.')}`;
  }

  const possibleTimes = Array.from(
    new Set(
      starters.reduce<Array<string>>((prev, s) => {
        return produce(prev, (draft) => {
          draft.push(...Array.from(Object.keys(s.times)));
        });
      }, []),
    ),
  );
  if (possibleTimes.length > 0) {
    csvString += `;times.${possibleTimes.join(';times.')}`;
  }

  if (user.admin) {
    csvString += ';player_link';
  }

  for (const starter of starters) {
    let row = [
      starter.startNumber,
      starter.name,
      starter.appId,
      starter.deviceType,
      starter.deviceClass,
      starter.markerColor,
      starter.startTime,
      starter.endTime,
      formatTimeDuration(starter.manualFinishDuration),
      starter.key,
      starter.id,
      starter.deviceId,
      starter.deviceName,
    ];

    if (possibleTags.length > 0) {
      row.push(...formatNestedObject(starter.tags, possibleTags));
    }
    if (possibleTimes.length > 0) {
      row.push(...formatNestedObject(starter.times, possibleTimes));
    }

    if (user.admin) {
      row.push(getPlayerUrl(event, { starterIds: [starter.id] }));
    }

    row = row.map((i) => (i == null ? '' : i));
    if (row.some((i) => i !== '')) {
      csvString += `\r\n${row.join(';').replace(/(\r\n|\n|\r)/gm, '')}`;
    }
  }

  return csvString;
}

function validateRawStarters(starters: Array<Partial<RacemapStarter>>): void {
  for (const starter of starters) {
    if (
      starter.deviceClass != null &&
      !Object.values(DeviceClasses).includes(starter.deviceClass)
    ) {
      throw new Error(
        `Participant ${
          starter.name || starter.appId || starter.id
        } has invalid device class. Possible devices classes are ${Object.values(
          DeviceClasses,
        ).join(',')}.`,
      );
    }

    if (starter.appId != null && !validateStarterAppId(starter.appId)) {
      throw new Error(
        `Participant ${
          starter.name || starter.appId || starter.id
        } has invalid app id. The app id has to be a valid IMEI, UUID or transponder id.`,
      );
    }
  }
}

export const useEventEditorActions = (): State['events']['actions'] =>
  useStore((s) => s.events.actions);
export const useLastStarterSyncReport = (): State['events']['lastStarterSyncReport'] =>
  useStore((s) => s.events.lastStarterSyncReport);
